import { FormProvider, Resolver, SubmitHandler, useForm } from "react-hook-form";
import images from "../../../assets/images";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from 'yup';
import { ReactNode, useEffect, useState } from "react";
import { FETCH_POLICY_NO_CACHE, messages, TAB_MAX_WIDTH } from "../../../utils/constants";
import FileUpload from "../../../components/Popups/FileUpload";
import ButtonStyle from "../../../components/ButtonStyle/ButtonStyle";
import ConfirmationPopup from "../../../components/ConfirmationPopup/ConfirmationPopup";
import './AdminAnnouncementForm.scss';
import { CREATE_ANNOUNCEMENT, DELETE_ANNOUNCEMENT, UPDATE_ANNOUNCEMENT } from "../api/AdminAnnouncementQuery";
import { useMutation } from "@apollo/client";
import ToasterWithMessage from "../../../components/toaster";
import { AnnouncementObjectType } from "../AdminAnnouncement";
import { useWindowWidth } from "../../../components/AdminDashboardTabsContentHeader/TabsContentHeader";
import { trackButtonClick } from "../../../firebase/analyticsHelper";
import { ButtonClickEvents } from "../../../firebase/constants/events";

interface PropsType {
  setDisplayForm: any;
  displayForm: boolean;
  setClickAction?: any;
  clickAction?: any;
  singleAnnouncementDetail?: AnnouncementObjectType;
}

interface IFormInput {
  title: string;
  message: string;
}

const schema = yup.object().shape({
  title: yup
    .string()
    .required("Title is required")
    .trim()
    // .matches(patterns?.alphaNumericSpace, messages?.title)
    // .matches(patterns?.NotStartWithNumber,messages?.titleNotStartWithNumber)
    // .min(5,messages?.titleLength)
    .max(100, messages?.titleLength),
  message: yup
    .string()
    .trim()
    .required("Message is required")
});

const AdminAnnouncementForm = ({ setDisplayForm, displayForm, setClickAction, clickAction, singleAnnouncementDetail }: PropsType) => {
  const { backArrow, teacherDeleteIcon, DeleteGif, solidinterfaceplus, AnnouncementImage, AddSubjectIcon, ImagePlaceholder } = images;
  const [profileImageURL, setProfileImageURL] = useState<string>();
  const { success, error } = ToasterWithMessage();
  const [open, setOpen] = useState<boolean>(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
  const baseUrl = process.env.REACT_APP_FRONTEND_URL;
  const width = useWindowWidth();

  const methods = useForm<IFormInput>({
    resolver: yupResolver(schema) as Resolver<IFormInput>,
    defaultValues: {
      title: '',
      message: ''
    },
  });

  const [editAnnouncement, { loading: editAnnouncementLoading }] = useMutation(UPDATE_ANNOUNCEMENT, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {
      success("Announcement updated successfully")
      setDisplayForm(!displayForm);
      setClickAction('');
      reset();
    },
    onError: (err) => {
      error(err?.message);
    }
  });

  const [deleteAnnouncement, { loading: deleteAnnouncementLoading }] = useMutation(DELETE_ANNOUNCEMENT, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {
      success("Announcement deleted successfully")
      setDisplayForm(!displayForm);
      setClickAction('');
      reset();
    },
    onError: (err) => {
      error(err?.message);
    }
  });

  const [createAnnouncement, { loading }] = useMutation(CREATE_ANNOUNCEMENT, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {
      success("Announcement added successfully")
      setDisplayForm(!displayForm);
      setClickAction('');
      reset();
    },
    onError: (err) => {
      error(err?.message)
    }
  });

  useEffect(() => {
    if (clickAction === 'edit') {
        reset({
          title: singleAnnouncementDetail?.aOriginalTitle,
          message: singleAnnouncementDetail?.aMessage,
        });
        setProfileImageURL(singleAnnouncementDetail?.aUrl)
    }
  },[clickAction]);

  const { handleSubmit, setFocus, reset, register, formState: { errors } } = methods;
    //To check the datas are differ 
    function areValuesDifferent(data: IFormInput) {
      const initialValues = {
        title: singleAnnouncementDetail?.aOriginalTitle,
        message: singleAnnouncementDetail?.aMessage,
        profileImage: singleAnnouncementDetail?.aUrl
      };
      // Compare classroomName, code, headTeacher and noofstudentaccess
      if (data.title !== initialValues.title) {
        return true;
      }
      if (data.message !== initialValues.message) {
        return true;
      }
      if (profileImageURL !== initialValues.profileImage) {
        return true;
      }
  
      return false;
    }
  const onSubmit: SubmitHandler<IFormInput> = data => {
    trackButtonClick(ButtonClickEvents?.CREATE_ANNOUNCEMENT_SAVE_BUTTON_CLICK, {
      description: "User clicks the save button on admin announcement form"
    });
    const variables = {
      input: {
          aTitle: data?.title?.trim(),
          aMessage: data?.message?.trim(),
          aUrl: profileImageURL
        }
    };
    const editVariables = {
      aId: singleAnnouncementDetail?.aId,
      input: {
          aTitle: data?.title?.trim(),
          aMessage: data?.message?.trim(),
          aUrl: profileImageURL,
      }
    };
    
    if (clickAction === 'edit' && (areValuesDifferent(data)) && editAnnouncementLoading === false && !isUploadOpen) {
      editAnnouncement({
        variables: editVariables
      })
    } else if(clickAction === 'edit' && (!areValuesDifferent(data))){
      error("There is no changes to update");
    }else if (clickAction === 'add' && loading === false && !isUploadOpen) {
      createAnnouncement({
        variables
      })
    }
  }

  //To delete the announcement
  const handleDelete = (aId: string | undefined) => {
    if (deleteAnnouncementLoading === false) {
      setOpenConfirmationPopup(true);
    }
  };

  // click yes button in confirmation popup
  const onYesButtonClick = () => {
    setOpenConfirmationPopup(false);
    deleteAnnouncement({
      variables: {
        aId: singleAnnouncementDetail?.aId
      }
    });
  }

  // click no button in confirmation popup
  const onNoButtonClick = () => {
    setOpenConfirmationPopup(false);
  }

  useEffect(() => {
    setFocus('title');
  }, [setFocus]);

  return (
    <div className='adminAnnouncementForm'>
      <div className='adminAnnouncementFormHeader'>
        <img
          onClick={() => { 
            setDisplayForm(!displayForm); 
            setClickAction(''); 
            reset(); 
          }}
          className="backArrow"
          alt="back-arrow"
          src={backArrow}
        />
        {width <= TAB_MAX_WIDTH && <div className="title">Add Announcement</div>}
      </div>
      <div className="adminAnnouncementFormContainer">
        <FormProvider {...methods}>
          <form className="addAnnouncementForm" onSubmit={handleSubmit(onSubmit)}>
            <div className={`addAnnouncementParentContainer ${Object.keys(errors).length ? 'hasErrorStyle' : ''}`}>
              <div className="announcementDetails">
                <div className="announcementTitleField">
                  <label className="titleOfAnnouncement">Title</label>
                  <div>
                  <input {...register('title')} className="titleInputStyle" placeholder="Title" type="text" />
                  {errors.title && <span className="error-message">{errors.title?.message as ReactNode}</span>}
                  </div>
                </div>
                <div className="announcementMessageField">
                  <label className="messageOfAnnouncement">Message</label>
                  <div>
                  <textarea {...register('message')} className={profileImageURL ? "messageInputStyleWithImg" : "messageInputStyle"} placeholder="Message" />
                  {errors?.message && <span className="error-message">{errors.message?.message as ReactNode}</span>}
                  </div>
                </div>
                <div className="announcementProfileImage">
                  <div 
                    onClick={() => {
                      setIsUploadOpen(true); 
                      setOpen(true);
                    }} 
                    className="uploadPhoto"
                  >
                    <span className="addPhoto">Add Photo</span>
                    <img
                      className="uploadButton"
                      alt="plus"
                      src={width <= TAB_MAX_WIDTH ? AddSubjectIcon : solidinterfaceplus}
                    />
                  </div>
                  {/* <div className={`${profileImageURL ? 'announcementEditContainer' : 'teacherImageContainer'}`}> */}
                   {profileImageURL 
                   ? <img 
                        className={`${profileImageURL ? 'announcementImageProfileStyle' : 'announcementImageDefault'}`} 
                        alt='AnnouncementImage' 
                        src={profileImageURL ? baseUrl + profileImageURL : AnnouncementImage} 
                      />
                   : width <= TAB_MAX_WIDTH 
                      ? <img src={ImagePlaceholder} width="120px" height="120px" className="image-placeholder" alt="Image Placeholder" /> 
                      : <></>
                  }
                  {/* </div> */}
                  {
                    isUploadOpen ?
                      <FileUpload 
                          page="Admin Announcement" 
                          type='profile' 
                          setUrl={setProfileImageURL} 
                          open={open} 
                          setOpen={setOpen} 
                          setIsUploadOpen={setIsUploadOpen} 
                          folderName="images" 
                      />
                      : null
                  }
                </div>
                {width <= TAB_MAX_WIDTH && (
                  clickAction !== 'edit' ?
                    <ButtonStyle 
                        paddingLeft='16px' 
                        paddingRight='15px' 
                        width='62px' 
                        height='23.92px' 
                        color='var(--color-black)' 
                        bgcolor="white" 
                        variant='outlined' 
                        text="Clear" 
                        onClick={() => { 
                          trackButtonClick(ButtonClickEvents?.CREATE_ANNOUNCEMENT_CLEAR_BUTTON_CLICK, { description: "User clicks the clear button on admin announcement form" }); 
                          reset(); 
                          setProfileImageURL(undefined); 
                        }} 
                        type="reset" 
                      />
                    : <ButtonStyle 
                        paddingLeft='16px' 
                        paddingRight='15px' 
                        width='62px' 
                        height='23.92px' 
                        color='var(--color-black)' 
                        bgcolor="white" 
                        variant='outlined' 
                        disableButton={deleteAnnouncementLoading||isUploadOpen} 
                        onClick={() => handleDelete(singleAnnouncementDetail?.aId)} 
                        deleteBtn={<img src={teacherDeleteIcon} alt="deleteIcon" className='deleteIconStyle' />} 
                      />
                )}
              </div>
              <div className="announcementFormButton">
                {width > TAB_MAX_WIDTH && (
                  clickAction !== 'edit' ?
                    <ButtonStyle 
                        paddingLeft='16px' 
                        paddingRight='15px' 
                        width='62px' 
                        height='23.92px' 
                        color='var(--color-black)' 
                        bgcolor="white" 
                        variant='outlined' 
                        text="Clear" 
                        onClick={() => { 
                          reset(); 
                          setProfileImageURL(undefined); 
                        }} 
                        type="reset" 
                    />
                    : <ButtonStyle 
                        paddingLeft='16px' 
                        paddingRight='15px' 
                        width='62px' 
                        height='23.92px' 
                        color='var(--color-black)' 
                        bgcolor="white" 
                        variant='outlined' 
                        disableButton={deleteAnnouncementLoading||isUploadOpen} 
                        onClick={() => handleDelete(singleAnnouncementDetail?.aId)} 
                        deleteBtn={<img src={teacherDeleteIcon} alt="deleteIcon" className='deleteIconStyle' />} 
                      />
                )}
                <ButtonStyle 
                    paddingLeft='26px' 
                    paddingRight='28px' 
                    width='83.25px' 
                    height='23.92px' 
                    disableButton={loading || editAnnouncementLoading||isUploadOpen} 
                    text="Save" 
                    type="submit" 
                />
              </div>
            </div>
          </form>
          <ConfirmationPopup
            open={openConfirmationPopup}
            message={`Are you sure you want to Delete this announcement?`}
            gifData={DeleteGif}
            yesButtonFunctionality={onYesButtonClick}
            noButtonFunctionality={onNoButtonClick}
            messageWidth={'195px'}
          />
        </FormProvider>
      </div>
    </div>
  )
}

export default AdminAnnouncementForm;
